import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button } from "@mui/material";
import { ArrowBackRounded } from "@mui/icons-material";
import {
  ErrorState,
  LoadingState,
  PageHeader,
} from "../Common/DocumentCraftingUi";
import {
  documentCraftingPaths,
  useDocumentCraftingAccess,
} from "../Common/documentCraftingNavigation";
import LabelSettings from "./LabelSettings";
import MyContactSettings from "./MyContactSettings";
import WorkspaceConfigForm from "./WorkspaceConfigForm";
import WorkspaceSwitcher from "./WorkspaceSwitcher";

/**
 * Settings of the module (`/document-crafting/settings`): everyone's contact
 * details and personal labels; project labels and workspace defaults are
 * read-only unless the user manages the workspace. Users without a workspace
 * (personal documents) only get their contact details and personal labels.
 * Platform admins pick the workspace they act on.
 */
const DocumentCraftingSettings: React.FC = () => {
  const { t } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  const access = useDocumentCraftingAccess();

  const header = (
    <PageHeader
      title={t("settings.title")}
      subtitle={t("settings.subtitle")}
      actions={
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
          {access.isAdmin && (
            <WorkspaceSwitcher currentWorkspaceId={access.workspaceId} />
          )}
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackRounded />}
            onClick={() => navigate(documentCraftingPaths.list)}
          >
            {t("nav.backToList")}
          </Button>
        </Box>
      }
    />
  );

  if (access.isLoading) {
    return (
      <>
        {header}
        <LoadingState />
      </>
    );
  }
  if (access.isError) {
    return (
      <>
        {header}
        <ErrorState onRetry={() => access.refetch()} />
      </>
    );
  }
  return (
    <>
      {header}
      {!access.hasWorkspace && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {access.isAdmin ? t("settings.noWorkspaceAdmin") : t("settings.personalScope")}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 5fr) minmax(0, 7fr)",
          },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
          <MyContactSettings />
          {access.hasWorkspace && <WorkspaceConfigForm />}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
          <LabelSettings
            canManageWorkspace={access.canManageWorkspace}
            hasWorkspace={access.hasWorkspace}
          />
        </Box>
      </Box>
    </>
  );
};

export default DocumentCraftingSettings;
